// File: src/api.js

import axios from 'axios';

// ─── 1) Environment Detection ─────────────────────────────────────────────────
const isDevelopment = process.env.NODE_ENV === 'development';
const isProduction = process.env.NODE_ENV === 'production';
const isTest = process.env.NODE_ENV === 'test';

// ─── 2) Determine API Base ─────────────────────────────────────────────────────
// Priority order:
// 1. REACT_APP_API_URL environment variable (for production)
// 2. /api (for local development with proxy)
// 3. https://intranet.nesimltd.co.ke/api (for test environment)
const API_ROOT = process.env.REACT_APP_API_URL
  ? process.env.REACT_APP_API_URL.replace(/\/+$/, '')
  : '';

const BASE_URL = API_ROOT
  ? `${API_ROOT}/api`
  : isTest 
    ? 'https://intranet.nesimltd.co.ke/api'
    : '/api';

// ─── 3) Log Configuration (Development Only) ──────────────────────────────────
if (isDevelopment) {
  console.log('🔧 API Configuration:', {
    NODE_ENV: process.env.NODE_ENV,
    REACT_APP_API_URL: process.env.REACT_APP_API_URL,
    API_ROOT,
    BASE_URL,
    isDevelopment,
    isProduction
  });
}

// ─── 4) Create Axios Instance ──────────────────────────────────────────────────
const instance = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: isProduction ? 30000 : 60000, // Longer timeout for development (60s) and production (30s)
});

// ─── 5) Request Interceptor ───────────────────────────────────────────────────
instance.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  // Log requests in development
  if (isDevelopment) {
    console.log(`🚀 API Request: ${config.method?.toUpperCase()} ${config.baseURL}${config.url}`);
  }
  
  return config;
}, error => {
  if (isDevelopment) {
    console.error('❌ Request Error:', error);
  }
  return Promise.reject(error);
});

// ─── 6) Response Interceptor ──────────────────────────────────────────────────
instance.interceptors.response.use(
  response => {
    // Log successful responses in development
    if (isDevelopment) {
      console.log(`✅ API Response: ${response.status} ${response.config.url}`);
    }
    return response;
  },
  error => {
    // Enhanced error logging
    if (isDevelopment) {
      console.error('❌ API Error:', {
        url: error.config?.url,
        method: error.config?.method,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
    }
    
    // Handle specific error cases
    if (error.response?.status === 401) {
      // Unauthorized - clear token and redirect to login
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    
    if (error.response?.status === 500) {
      console.error('🚨 Server Error - Check backend logs');
    }
    
    return Promise.reject(error);
  }
);

// ─── 7) Named Helpers ─────────────────────────────────────────────────────────
export default instance;
export const get  = (url, cfg) => instance.get(url, cfg);
export const post = (url, data, cfg) => instance.post(url, data, cfg);
export const put  = (url, data, cfg) => instance.put(url, data, cfg);
export const del  = (url, cfg) => instance.delete(url, cfg);

// ─── 8) Environment Info ──────────────────────────────────────────────────────
export const getApiInfo = () => ({
  baseURL: BASE_URL,
  environment: process.env.NODE_ENV,
  apiRoot: API_ROOT,
  isDevelopment,
  isProduction
});
